import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Button,
  Spin,
  Col,
  Modal,
  Row,
  DatePicker,
  Input,
  message,
} from 'antd';
import Select from 'components/Select';
import { onError } from 'utils/handlers';
import FormSubmitControls from 'components/FormSubmitControls';
import Table from 'components/Table';
import ModalDelete from 'components/ModalDelete';
import NumericInput from 'components/NumericInput';
import { InputSearchPro } from 'components/InputSearch';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';
import {
  deleteActividadEconomicaDeContribuyente,
  getActividadesEconomicasDeContribuyente,
  patchActividadEconomicaDeContribuyente,
  postActividadEconomicaDeContribuyente,
} from 'api/empresas/actividadesEconomicasDeContribuyente';
import {
  deleteActividadDeContribuyente,
  getActividadesDeContribuyente,
  patchActividadDeContribuyente,
  postActividadDeContribuyente,
} from 'api/empresas/actividadesDeContribuyente';
import { getActividadEconomica, getActividadesEconomicas, getCatalogoActividadesEconomicas } from 'api/empresas';
import BusquedaAvanzadaActividades from 'components/BusquedaAvanzadaActividades';

export const model = 'actividadeconomicadecontribuyente';
export const catalogsModels = ['actividadeconomica'];

export const permissionActividadesEconomicasContribuyentes = {
  title: 'Actividades Económicas (SAT)',
  model,
  catalogs: catalogsModels,
};

export const permissionActividadesContribuyentes = {
  title: 'Actividades Económicas',
  model: 'actividaddecontribuyente',
  catalogs: ['actividadeconomica'],
};

const ActividadesEconomicas = ({
  contribuyente,
  permission,
  sat,
  mutable,
  fetchCallback,
  callback,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedActividad, setSelectedActividad] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAdvanced, setvisibleAdvanced] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const actividades = useRef({ results: [] });
  const [actividadesTemp, setActividadesTemp] = useState({ results: [] });

  const [data, setData] = useState([]);
  const [actividadFound, setActividadFound] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    let _data = null;
    if (sat) {
      _data = await getActividadesEconomicasDeContribuyente({ contribuyente });
    } else {
      _data = await getActividadesDeContribuyente({ contribuyente });
    }
    if (fetchCallback) {
      fetchCallback(_data);
    }
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const fetch = async () => {
      setLoading(true);
      let _actividadesCont = null;
      let _actividades = [];
      if (sat) {
        _actividadesCont = await getActividadesEconomicasDeContribuyente({ contribuyente });
        // SE OBTENDRÁN LAS ACTIVIDADES ECONÓMICAS
        _actividades = await getActividadesEconomicas();
      } else {
        _actividadesCont = await getActividadesDeContribuyente({ contribuyente });
        // _actividades = await getCatalogoActividadesEconomicas({ page: 1 });
      }
      if (mounted) {
        if (fetchCallback) {
          fetchCallback(_actividadesCont);
        }
        setData(_actividadesCont);
        actividades.current = _actividades;
        setLoading(false);
      }
    };
    fetch();
    return () => { mounted = false; };
  }, []);

  const fetchActividades = async (value) => {
    setLoading(true);
    const _value = await getActividadEconomica(value?.id || value);
    if (_value) {
      setActividadFound({
        ..._value,
        sub_grupo: _value?.sub_grupo?.descripcion,
        grupo: _value?.sub_grupo?.grupo?.descripcion,
        descripcion: `${_value?.clave} - ${_value?.descripcion}`,
      });
      form.setFieldsValue({
        actividad_economica: _value.id,
        sub_grupo: _value?.sub_grupo?.descripcion,
        grupo: _value?.sub_grupo?.grupo?.descripcion,
      });
    } else {
      setActividadFound([]);
    }
    setLoading(false);
  };

  const onCancel = () => {
    form.resetFields();
    setVisible(false);
    setSelectedRowKeys([]);
    setSelectedActividad([]);
    setActividadFound([]);
    setActividadesTemp({ results: [] });
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      values.contribuyente = contribuyente;
      let saved = null;
      if (sat) {
        // PERTENECE A SAT
        saved = await (key
          ? patchActividadEconomicaDeContribuyente(key, values)
          : postActividadEconomicaDeContribuyente(values));
      } else {
        values.actividad = key ? selectedActividad?.actividad?.id : values.actividad;
        // PERTENECE A CATALOGO DE LA ENTIDAD
        saved = await (key
          ? patchActividadDeContribuyente(key, values)
          : postActividadDeContribuyente(values));
      }
      if (saved) {
        if (callback) {
          const actividad = actividadFound || actividades.results
            .find((a) => a.id === saved.actividad_economica);
          saved.message = `Actividad ${actividad.descripcion} ${key ? 'agregada' : 'editada'}`;
          callback(saved);
        }
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    setSelectedActividad(record);
    form.setFieldsValue(record);
    if (!sat) form.setFieldsValue({ actividad: record?.actividad?.id });
  };

  const onClickEdit = async () => {
    if (sat) await fetchActividades(selectedActividad?.actividad_economica);
    else setActividadesTemp({ results: [selectedActividad.actividad] });
    setVisible(true);
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickDelete = () => {
    setVisibleAlert(true);
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    let deleted = null;
    if (sat) {
      deleted = await deleteActividadEconomicaDeContribuyente(key);
    } else {
      deleted = await deleteActividadDeContribuyente(key);
    }
    if (deleted) {
      if (callback) {
        const match = data.find((e) => e.id === key);
        const actividad = actividadFound || actividades.results
          .find((a) => a.id === match.actividad_economica);
        callback({ message: `Actividad ${actividad.descripcion} eliminada` });
      }
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const rowSelection = mutable ? {
    selectedRowKeys,
    type: 'radio',
  } : null;

  const columns = [
    {
      titleText: 'Actividad Económica',
      key: `${sat ? 'actividad_economica' : 'actividad'}`,
      dataIndex: `${sat ? 'actividad_economica' : 'actividad'}`,
      width: 300,
      render: (val) => `${val?.actividad?.descripcion || val?.descripcion}`,
    },
    {
      titleText: 'Fecha Inicio',
      key: 'fecha_inicio',
      dataIndex: 'fecha_inicio',
      width: 200,
      render: (val) => val?.format(),
    },
    {
      titleText: 'Porcentaje de Actividad',
      key: 'porcentaje_de_actividad',
      dataIndex: 'porcentaje_de_actividad',
      width: 200,
      render: (val) => `${val}%`,
    },
  ];

  const rules = {
    required: { required: true },
    porcentaje: [
      { required: true },
      {
        validator: async (_, _value) => {
          if (_value) {
            const value = typeof _value === 'string' ? parseFloat(_value) : _value;
            if (value > 100) {
              throw new Error('No se puede superar el 100%');
            }
            if (!selectedRowKeys.length
              && (data.reduce((prev, curr) => prev + curr
                .porcentaje_de_actividad, 0) + value) > 100) {
              throw new Error('La suma de los porcentajes no puede superar el 100%');
            }
          }
        },
      },

    ],
  };

  const onSearch = async () => {
    try {
      setLoading(true);
      await form.validateFields(['q']);
      const q = form.getFieldsValue(['q']);
      const _actividades = await getActividadesEconomicasDeContribuyente(q);
      if (_actividades.length === 1) {
        form.setFieldsValue({
          actividad_economica: _actividades?.id,
          sub_grupo: _actividades?.sub_grupo?.descripcion,
          grupo: _actividades?.sub_grupo?.grupo?.descripcion,
        });
        setActividadFound(_actividades);
      } else if (!_actividades.length) {
        message.info('No se encontraron coincidencias');
        setActividadFound([]);
      } else {
        message.info('Se encontraron varias coincidencias sea más específico');
      }
      setLoading(false);
    } catch (error) {
      onError(error);
      setLoading(false);
    }
  };

  const resetAndValidateActividad = () => {
    setActividadFound([]);
    form.resetFields(['descripcion']);
    form.validateFields(['descripcion']);
  };

  return (
    <Spin key={sat ? 'economicas sat' : 'economicas a secas'} tip="Cargando..." spinning={loading}>
      <Table
        cols={columns}
        data={data}
        rowSelection={rowSelection}
        handleOnRowClick={handleOnRowClick}
        controls={mutable ? {
          onClickAdd,
          onClickEdit,
          onClickDelete,
        } : { onClickExpand: onClickEdit }}
        allowSort={false}
        allowSearch={false}
        rowKey="id"
        permission={permission}
        hasAllViewPermission
      />
      <Modal
        visible={visible}
        closable={false}
        footer={null}
        title={(
          <FormSubmitControls
            label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Actividad`}
            onFinish={mutable ? onFinish : null}
            onCancel={onCancel}
            loading={loading}
          />
        )}
        keyboard={!loading}
        maskClosable={!loading}
        onCancel={onCancel}
      >
        <Col span={24}>
          <Form
            form={form}
            layout="vertical"
            onFinish={() => onFinish()}
          >
            <Row gutter={10}>
              {sat && (
                <>
                  <InputSearchPro
                    label="Actividad"
                    tooltip="Búsqueda de actividades económicas"
                    name="q"
                    onClickAdvanced={() => setvisibleAdvanced(true)}
                    breakPoints={{
                      xs: 24,
                      sm: 24,
                      md: 24,
                    }}
                    inputProps={{
                      onPressEnter: (val) => onSearch(val),
                      onChange: ({ target: { value } }) => {
                        if (!value) resetAndValidateActividad();
                      },
                    }}
                    resultProps={{
                      name: 'actividad_economica',
                      label: 'Actividad',
                      dataSource: actividadFound,
                      onClear: resetAndValidateActividad,
                      breakPoints: {
                        xs: 24,
                        sm: 24,
                        md: 24,
                      },
                    }}
                    disabled={!mutable}
                  />
                  <Col span={24}>
                    <Form.Item
                      name="grupo"
                      label="Grupo"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                  <Col span={24}>
                    <Form.Item
                      name="sub_grupo"
                      label="Subgrupo"
                    >
                      <Input disabled />
                    </Form.Item>
                  </Col>
                </>
              )}
              {!sat && (
                <>
                  <Col span={24}>
                    <Form.Item
                      name="actividad"
                      label="Actividad"
                    >
                      <Select
                        dataSource={actividadesTemp.results.length
                          ? actividadesTemp : actividades.current}
                        usingExternalDataSource={Boolean(actividadesTemp.results.length)}
                        onClear={() => {
                          form.resetFields(['actividad']); setActividadesTemp({ results: [] });
                        }}
                        pagination={{
                          getter: getCatalogoActividadesEconomicas,
                          setter: (dataSource) => { actividades.current = dataSource; },
                          config: { auto: true, qName: 'descripcion' },
                        }}
                        disabled={!mutable}
                      />
                    </Form.Item>
                  </Col>
                </>
              )}
              <Col span={24}>
                <NumericInput
                  name="porcentaje_de_actividad"
                  label="Porcentaje de Actividad"
                  rules={rules.porcentaje}
                  decimal
                  suffix="%"
                  disabled={!mutable}
                />
              </Col>
              <Col span={24}>
                <Form.Item
                  name="fecha_inicio"
                  label="Fecha de Inicio de la Actividad"
                >
                  <DatePicker
                    disabled={!mutable}
                    placeholder=""
                    format={DATE_FRONTEND_FORMAT}
                  />
                </Form.Item>
              </Col>
              <Col span={24}>
                <Form.Item
                  name="fecha_fin"
                  label="Fecha de Fin"
                >
                  <DatePicker
                    disabled={!mutable}
                    placeholder=""
                    format={DATE_FRONTEND_FORMAT}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Col>
        <BusquedaAvanzadaActividades
          visible={visibleAdvanced}
          setVisible={setvisibleAdvanced}
          callback={fetchActividades}
        />
      </Modal>
      <ModalDelete
        onDelete={deleteItem}
        onCancel={() => {
          setVisibleAlert(false);
          setSelectedRowKeys([]);
        }}
        visible={visibleAlert}
        content=" el registro seleccionado?"
        loading={loading}
      />
    </Spin>
  );
};

ActividadesEconomicas.propTypes = {
  contribuyente: PropTypes.number,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
  sat: PropTypes.bool,
  mutable: PropTypes.bool,
  fetchCallback: PropTypes.func,
  callback: PropTypes.func,
};

ActividadesEconomicas.defaultProps = {
  contribuyente: null,
  sat: false,
  mutable: true,
  fetchCallback: null,
  callback: null,
};

export default ActividadesEconomicas;
